/* eslint-disable no-magic-numbers */
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { Button } from '@/shared/Button';
import {
  getModelElementsForModel,
  updateElementMeta,
} from '@/redux/modelElements/modelElements.thunks';
import { showToast } from '@/utils/showToast';
import { closeModal } from '@/redux/modal/modal.slice';
import { SerializedError } from '@reduxjs/toolkit';
import { elementMetaStateSelector } from '@/redux/modal/modal.selector';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { LoadingIndicator } from '@/shared/LoadingIndicator';
import { Input } from '@/shared/Input';
import { modelElementsByModelIdSelector } from '@/redux/modelElements/modelElements.selector';

export type ElementMetaFormHandles = {
  clearMapModelInput: () => void;
};

type ElementMetaFormProps = {
  immediateLink: string;
  setImmediateLink: (immediateLink: string) => void;
};

export const ElementMetaDataForm = forwardRef<ElementMetaFormHandles, ElementMetaFormProps>(
  ({ immediateLink, setImmediateLink }, ref) => {
    const elementMetaState = useAppSelector(elementMetaStateSelector);
    const modelElement = useAppSelector(state =>
      modelElementsByModelIdSelector(state, elementMetaState?.model || 0),
    ).filter(element => element.id === elementMetaState?.id)[0];

    const [isSending, setIsSending] = useState<boolean>(false);

    const dispatch = useAppDispatch();

    const handleImmediateLinkChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      setImmediateLink(e.target.value);
    };

    const handleSubmit = async (): Promise<void> => {
      if (!elementMetaState || !modelElement) {
        return;
      }
      setIsSending(true);
      try {
        await dispatch(
          updateElementMeta({
            element: modelElement,
            data: {
              immediateLink,
            },
          }),
        ).unwrap();
        showToast({
          type: 'success',
          message: 'The element glyph has been successfully updated.',
        });
        dispatch(getModelElementsForModel(elementMetaState.model));
        dispatch(closeModal());
      } catch (error) {
        const typedError = error as SerializedError;
        showToast({
          type: 'error',
          message: typedError.message || 'An error occurred while updating a glyph.',
        });
      } finally {
        setIsSending(false);
      }
    };

    useImperativeHandle(ref, () => ({
      clearMapModelInput: (): void => {},
    }));

    return (
      <div>
        {isSending && (
          <div className="c-element-glyph-modal-loader">
            <LoadingIndicator width={44} height={44} />
          </div>
        )}
        <div className="grid grid-cols-3 gap-2">
          <div className="mb-4 flex flex-col gap-2">
            <span>Immediate link:</span>
            <Input
              type="text"
              name="immediateLink"
              id="immediateLink"
              data-testid="immediate-link-name"
              value={immediateLink || ''}
              onChange={handleImmediateLinkChange}
              sizeVariant="medium"
              className="mt-2.5 text-xs"
            />
          </div>
          <div />
          <div className="mb-4 flex flex-col gap-2 align-bottom">
            <Button
              type="button"
              className="mt-[28px] justify-center text-base font-medium"
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    );
  },
);

ElementMetaDataForm.displayName = 'ElementMetaDataForm';
